// ─────────────────────────────────────────────────────────────────────────
// mapsExport - the one blessed bridge: hand the day's ordered stops to
// Google Maps for real, traffic-aware turn-by-turn. This is a USER-INITIATED
// export (the rep taps "Open in Google Maps"), which the boardroom ruled is
// the sanctioned bridge, not a no-PII-in-URL violation.
//
// This is DISTINCT from WEDGE_DRIVE_EXPORT_V1 (which exports a brief to
// Google DRIVE as a doc). Different bridge, different flag. Do not conflate.
//
// buildLegLinks() is the SECOND bridge: N-1 two-point legs, one per hop, so an
// iPhone rep lands in Apple Maps (which has no multi-waypoint URL scheme) and
// so a long day is never truncated by Google's waypoint cap.
//
// Security hardening (boardroom, Troy Hunt + Max Schrems, one code path):
//   1. Range-validate EVERY lat/lng before it touches the URL string.
//      Brief-supplied coords are attacker-influenceable input flowing into
//      maps/dir/?waypoints= (injection / open-redirect vector).
//   2. Google silently caps ~10 waypoints. Apply a DOCUMENTED truncation
//      rule (keep origin + first N-1 by order, drop the tail) and report
//      what was dropped, so a long day is never a silent surprise.
//   3. scrubCoords() strips coords from any analytics/Sentry payload on the
//      export tap, so the same coords never land in a breadcrumb.
// ─────────────────────────────────────────────────────────────────────────

import { hasValidCoords } from './sequencer.js';

// Google Maps consumer directions cap: origin + destination + up to ~8
// intermediate waypoints. We keep the total at 10 addressable points.
export const MAX_WAYPOINTS = 10;
const MAPS_DIR_BASE = 'https://www.google.com/maps/dir/?api=1';

// Round coords for the URL to ~5 decimals (~1.1m) - enough to route to the
// door, without emitting spurious precision.
function fmt(n) {
  return Number(n.toFixed(5)).toString();
}

/**
 * @typedef {Object} MapsExport
 * @property {string|null} url        - the maps/dir URL, or null if <2 valid stops
 * @property {number} used            - stops included in the URL
 * @property {number} dropped         - stops truncated past MAX_WAYPOINTS
 * @property {number} invalid         - stops rejected for bad coords
 * @property {string[]} droppedNames  - names of truncated stops (for honest disclosure)
 */

/**
 * Build a Google Maps directions URL from ordered stops. Order is PRESERVED
 * exactly (the consumer deep link does not re-sequence - that is why the
 * in-Wedge suggested order matters). Invalid coords are dropped up front;
 * if more than MAX_WAYPOINTS valid stops remain, the tail is truncated per
 * the documented rule and reported in droppedNames.
 *
 * @param {import('./sequencer.js').Stop[]} orderedStops
 * @returns {MapsExport}
 */
export function buildMapsUrl(orderedStops) {
  const list = Array.isArray(orderedStops) ? orderedStops : [];
  const valid = list.filter(hasValidCoords);
  const invalid = list.length - valid.length;

  if (valid.length < 2) {
    return { url: null, used: 0, dropped: 0, invalid, droppedNames: [] };
  }

  // Documented truncation for Google's ~10-waypoint cap. A booked appointment
  // (hard pin) is NEVER dropped, so we keep ALL pins first, then fill the
  // remaining slots with the earliest free stops in sequence order, and drop
  // the tail of free stops. The rep is told which stops were dropped so they
  // can run a second leg.
  let kept = valid;
  let droppedNames = [];
  if (valid.length > MAX_WAYPOINTS) {
    const isPin = (s) => Number.isFinite(s.apptStart);
    const pins = valid.filter(isPin);
    const free = valid.filter((s) => !isPin(s));
    const freeSlots = Math.max(0, MAX_WAYPOINTS - pins.length);
    const keptSet = new Set([...pins, ...free.slice(0, freeSlots)]);
    kept = valid.filter((s) => keptSet.has(s)); // preserve the suggested order
    // Extreme edge: more hard pins than slots. Keep the earliest MAX pins in
    // order rather than overflow the URL (still never silently favors a free
    // stop over a booked one).
    if (kept.length > MAX_WAYPOINTS) kept = kept.slice(0, MAX_WAYPOINTS);
    const keptFinal = new Set(kept);
    droppedNames = valid.filter((s) => !keptFinal.has(s)).map((s) => String(s.name || 'stop'));
  }

  const origin = kept[0];
  const destination = kept[kept.length - 1];
  const waypoints = kept.slice(1, -1);

  const params = [
    MAPS_DIR_BASE,
    `origin=${encodeURIComponent(`${fmt(origin.lat)},${fmt(origin.lng)}`)}`,
    `destination=${encodeURIComponent(`${fmt(destination.lat)},${fmt(destination.lng)}`)}`,
  ];
  if (waypoints.length) {
    const wp = waypoints.map((s) => `${fmt(s.lat)},${fmt(s.lng)}`).join('|');
    params.push(`waypoints=${encodeURIComponent(wp)}`);
  }
  params.push('travelmode=driving');

  return {
    url: params.join('&'),
    used: kept.length,
    dropped: valid.length - kept.length,
    invalid,
    droppedNames,
  };
}

/**
 * @typedef {Object} LegLink
 * @property {Object} from     - the origin stop
 * @property {Object} to       - the destination stop
 * @property {string} fromName - honest display name for the origin
 * @property {string} toName   - honest display name for the destination
 * @property {string} url      - a TWO-POINT native-maps directions URL
 */

/**
 * Build N-1 two-point navigation legs for the ordered day.
 *
 * Why this exists: buildMapsUrl hands the WHOLE day to Google web maps and is
 * capped at MAX_WAYPOINTS. Apple Maps has NO multi-waypoint URL scheme at all,
 * so an iPhone rep (the core user) gets bounced out of their default nav into
 * Google's web map. A two-point leg is the ONE shape every platform supports
 * natively, so leg-by-leg opens the rep's own maps app - and because each leg
 * is its own URL there is NO waypoint cap on the day.
 *
 * Security: coords are attacker-influenceable brief data flowing into a URL,
 * so every endpoint goes through the same hasValidCoords range check and the
 * same 5-decimal fmt() rounding as buildMapsUrl. A stop with invalid coords is
 * dropped up front, so no leg is ever built against it (the remaining valid
 * stops chain to each other - the rep still has to drive that hop).
 *
 * @param {import('./sequencer.js').Stop[]} orderedStops
 * @param {'apple'|'google'} platform
 * @returns {LegLink[]} empty when fewer than 2 stops carry valid coords
 */
export function buildLegLinks(orderedStops, platform) {
  const list = Array.isArray(orderedStops) ? orderedStops : [];
  const valid = list.filter(hasValidCoords);
  if (valid.length < 2) return [];

  const apple = String(platform || '').toLowerCase() === 'apple';
  const legs = [];
  for (let i = 1; i < valid.length; i++) {
    const from = valid[i - 1];
    const to = valid[i];
    const a = `${fmt(from.lat)},${fmt(from.lng)}`;
    const b = `${fmt(to.lat)},${fmt(to.lng)}`;
    const url = apple
      ? `https://maps.apple.com/?saddr=${encodeURIComponent(a)}&daddr=${encodeURIComponent(b)}&dirflg=d`
      : `${MAPS_DIR_BASE}&origin=${encodeURIComponent(a)}&destination=${encodeURIComponent(b)}&travelmode=driving`;
    legs.push({
      from,
      to,
      fromName: String(from.name || 'Start'),
      toName: String(to.name || 'Stop'),
      url,
    });
  }
  return legs;
}

/**
 * Remove any lat/lng/coord fields from an analytics/telemetry payload before
 * it is emitted on the export tap, so a rep's destinations never land in a
 * Sentry breadcrumb or analytics event. Returns a shallow-cleaned copy;
 * recurses one level into plain-object/array values (stop lists).
 *
 * @param {Object} payload
 * @returns {Object}
 */
export function scrubCoords(payload) {
  const COORD_KEYS = new Set(['lat', 'lng', 'latitude', 'longitude', 'coords', 'coordinate', 'coordinates', 'geometry', 'location']);
  const clean = (v) => {
    if (Array.isArray(v)) return v.map(clean);
    if (v && typeof v === 'object') {
      const out = {};
      for (const k of Object.keys(v)) {
        if (COORD_KEYS.has(k)) continue;
        out[k] = clean(v[k]);
      }
      return out;
    }
    return v;
  };
  return clean(payload && typeof payload === 'object' ? payload : {});
}
